import { useMemo } from 'react'
import { Button } from '@cocoonspace/ui/components/button'
import { Separator } from '@cocoonspace/ui/components/separator'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import {
	CalendarIcon,
	CheckIcon,
	ClockIcon,
	type LucideIcon,
} from 'lucide-react'

import { type Step, useBookingVisitStore } from './space-visit.store'

const iconRefs: Record<Step, LucideIcon> = {
	day: CalendarIcon,
	intro: CalendarIcon,
	payment: CheckIcon,
	startTime: ClockIcon,
}

const StepBtn = ({ step, activeStep }: { step: Step; activeStep: Step }) => {
	const { t } = useLingui()
	const setStep = useBookingVisitStore.use.setStep()

	const disabled = useMemo(() => {
		if (step === 'startTime') return ['day'].includes(activeStep)
		if (step === 'payment') return ['day', 'startTime'].includes(activeStep)
		return false
	}, [activeStep, step])

	const labelRefs = useMemo(
		() => ({
			day: t`Date`,
			intro: '',
			payment: t`Confirmation`,
			startTime: t`Créneau`,
		}),
		[t],
	)

	const Icon = iconRefs[step]

	return (
		<Button
			className='relative !size-10 p-0'
			disabled={disabled}
			onClick={() => setStep(step)}
			variant={activeStep === step ? 'default' : 'outline'}
		>
			<Icon />

			<span
				className={cn(
					'text-foreground disabled:text-foreground/50 absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs font-normal',
					{ 'text-primary': activeStep === step },
				)}
			>
				{labelRefs[step]}
			</span>
		</Button>
	)
}

export const StepperIndicator = () => {
	const activeStep = useBookingVisitStore.use.step()

	if (activeStep === 'intro') return null

	return (
		<div className='my-2 flex items-center gap-4 pb-4'>
			<StepBtn
				activeStep={activeStep}
				step='day'
			/>

			<Separator
				className={cn('flex-1', {
					'bg-foreground': ['startTime', 'payment'].includes(activeStep),
				})}
			/>

			<StepBtn
				activeStep={activeStep}
				step='startTime'
			/>

			<Separator
				className={cn('flex-1', {
					'bg-foreground': activeStep === 'payment',
				})}
			/>

			<StepBtn
				activeStep={activeStep}
				step='payment'
			/>
		</div>
	)
}
